import {
    useAuthStore,
    userStore,
    useFormResponseStore,
    useValidationErrorsStore,
    useFormStore
} from '@/store';
import {
    useGetAllUsers,
    useGetAllForms,
    useCreateApplicantProcess,
    useSubmitFormResponse,
    useGetProcessAndForm
} from '@/hooks';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { formStore } from '@/store/form/form-store';
import { useNavigate, useParams } from 'react-router-dom';
import SearchUser from '@/components/settings/search-user';
import React, { FormEvent, useEffect, useState } from 'react';
import FormAnswer from '@/components/form/form-answer/form-answer';
import { useFormDraftStorage } from '@/hooks/form/use-form-draft-storage';
import { useQueryClient } from '@tanstack/react-query';
import type { TriggerRun } from '@/types/process-trigger.types';
import { isRunOpen } from '@/hooks/process-trigger/use-process-trigger';

interface AnswerApplicationPageProps {
    // Set when answering a pending start from a process trigger (route /to-do/:runId).
    triggerRun?: TriggerRun;
}

const AnswerApplicationPage: React.FC<AnswerApplicationPageProps> = ({ triggerRun }) => {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const { setUsers } = userStore()
    const { setFormList } = formStore();
    const { formResponses, clearFormResponses } = useFormResponseStore();
    const { validationErrors, clearValidationErrors } = useValidationErrorsStore();
    const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
    const [selectedNextStaffId, setSelectedNextStaffId] = useState()

    const submitResponseMutation = useSubmitFormResponse();
    const createApplicantProcessMutation = useCreateApplicantProcess();

    const { setFormId, setSections, sections, clearForm } = useFormStore();
    const params = useParams();
    const queryClient = useQueryClient();
    const processId = triggerRun?.targetProcess.id ?? (params?.processId as string);
    const formId = triggerRun?.targetProcess.firstFormId ?? (params?.formId as string);
    const { clearDraft } = useFormDraftStorage({
        userId: user?.id,
        processId,
        formId,
    });

    const [formName, setFormName] = useState("");
    const [isInitialized, setIsInitialized] = useState(false);
    const [navigationHandlers, setNavigationHandlers] = useState<{
        handlePreviousSection: () => void;
        handleNextSection: () => void;
        canGoBack: boolean;
        canGoNext: boolean;
    } | null>(null);

    const { data: formNameData } = useGetAllForms();

    const { data: processAndForm } = useGetProcessAndForm(processId, formId);

    // Find the specific process form by formId
    const processForm = processAndForm?.data?.forms?.find((pf: any) => pf.formId === formId);

    const staticUsersQuery = useGetAllUsers({ names: ["Staff"] });
    const dynamicUsersQuery = useGetAllUsers({ roleIds: processForm?.nextStepRoles });

    useEffect(() => {
        if (processForm?.nextStepType === "STATIC") {
            setUsers(staticUsersQuery.data)
        } else if (processForm?.nextStepType === "DYNAMIC" && processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") {
            setUsers(dynamicUsersQuery.data)
        }
    }, [staticUsersQuery.data, dynamicUsersQuery.data, processForm])

    const navigate = useNavigate();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (window.sessionStorage.getItem('flightPlanNavigationInProgress') === '1') {
                const timer = window.setTimeout(() => {
                    window.sessionStorage.removeItem('flightPlanNavigationInProgress');
                }, 0);
                return () => window.clearTimeout(timer);
            }
        }
    }, []);

    // Reset all form state when component unmounts or formId changes
    useEffect(() => {
        return () => {
            if (typeof window !== 'undefined') {
                const isFlightPlanNavigation =
                    window.sessionStorage.getItem('flightPlanNavigationInProgress') === '1';

                if (isFlightPlanNavigation) {
                    return;
                }
            }

            clearForm();
            clearFormResponses();
            clearValidationErrors();
        };
    }, [formId]);

    useEffect(() => {
        if (formNameData) {
            setFormList(formNameData);
        }
    }, [formNameData, setFormList]);

    useEffect(() => {
        if (!formId || !formNameData || !processAndForm) return;

        // Only proceed if we have all required data
        const foundForm = formNameData?.find((form: any) => form.id === formId);
        if (!foundForm) {
            toast.error(t('answerApplication.formNotFound'));
            navigate('/');
            return;
        }
        // console.log(foundForm)
        // Initialize form state
        setSections(foundForm.design);
        setFormId(formId);
        setFormName(foundForm.name);
        setIsInitialized(true);

    }, [formId, formNameData, processAndForm, setSections, setFormId, navigate]);

    const handleSubmit = async (event: FormEvent<HTMLButtonElement>) => {
        event.preventDefault();

        // Validate user is logged in
        if (!user?.id) {
            toast.error(t('answerApplication.mustBeLoggedIn'));
            return;
        }

        // Validate form responses - check errors in the order questions appear
        let firstError: string | null = null;
        
        // Iterate through sections and questions in order
        for (const section of sections || []) {
            const sectionErrors = validationErrors[section.id] || {};
            for (const question of section.questions) {
                const error = sectionErrors[question.id];
                if (error !== null && error !== undefined) {
                    firstError = error;
                    break;
                }
            }
            if (firstError) break;
        }
        
        if (firstError) {
            toast.error(firstError);
            return;
        }

        if ((processForm?.nextStepType === "DYNAMIC" &&
            processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") && !selectedNextStaffId) {
            toast.error(t('answerApplication.selectStaffToSubmit'));
            return;
        }

        try {
            // Transform formResponses to match bulk upload format
            // Convert from: { sectionId: { questions: { questionId: {...} } } }
            // To: [{ sectionId, sectionName, responses: [...] }]
            const transformedResponses = Object.values(formResponses).map(section => ({
                sectionId: section.sectionId,
                sectionName: section.sectionName,
                responses: Object.values(section.questions)
            }));

            // Submit formResponses in array format (consistent with bulk upload)
            const apResponse = await createApplicantProcessMutation.mutateAsync({
                applicantId: user.id,
                processId,
                formId,
                nextStaffId: selectedNextStaffId,
                responses: transformedResponses, // Now using transformed array format
                triggerRunId: triggerRun?.id,
            });

            if (apResponse) {
                clearForm();
                clearFormResponses();
                clearValidationErrors();
                clearDraft();
                toast.success(t('common.applicationSubmittedSuccessfully'));
                if (triggerRun) {
                    // Back to To do only while it still has items; its tab hides once empty.
                    const remaining = (queryClient.getQueryData<TriggerRun[]>(['trigger-runs', 'mine']) ?? [])
                        .filter((run) => run.id !== triggerRun.id && isRunOpen(run)).length;
                    queryClient.invalidateQueries({ queryKey: ['trigger-runs'] });
                    navigate(remaining > 0 ? '/to-do' : '/');
                } else {
                    navigate('/');
                }
            } else {
                toast.error(t('answerApplication.failedToCreateProcess'));
            }

        } catch (error) {
            console.error('Submission error:', error);
            toast.error(t('answerApplication.submissionError'));
        }
    };

    if (!isInitialized) {
        return (
            <div className="flex justify-center items-center py-20">
                <p className="text-center py-8 text-primary">{t('answerApplication.loadingForm')}</p>
            </div>
        );
    }

    return (
        <form className="p-4 lg:p-8 space-y-6 max-w-screen-lg mx-auto flex flex-col h-full">
            {triggerRun && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    <p className="font-medium">
                        {t('processTriggers.todo.startedAfter', {
                            process: triggerRun.parentApplicantProcess.process.name,
                        })}
                    </p>
                    {triggerRun.trigger?.message && (
                        <p className="mt-1 whitespace-pre-line">{triggerRun.trigger.message}</p>
                    )}
                </div>
            )}
            <h1 className="text-2xl font-medium text-[#001A55] text-center">{formName}</h1>
            <FormAnswer 
                setCurrentFormSectionId={setCurrentSectionId}
                onNavigationHandlersReady={setNavigationHandlers}
            />

            {sections && sections.length > 0 && sections.findIndex((section) => section.id === currentSectionId) === sections.length - 1 && (
                <div className='md:px-4 space-y-4'>
                    {(processForm?.nextStepType === "DYNAMIC" && processForm?.nextStepSpecifiedTo === "SINGLE_STAFF") ? (
                        <div className='max-w-md space-y-1'>
                            <div className='main-label font-medium'>{t('answerApplication.selectStaffToSubmit')}</div>
                            <SearchUser setUserId={setSelectedNextStaffId} />
                        </div>
                    ) : (
                        <div></div>
                    )}

                    <div className="flex space-x-4 items-center">
                        {navigationHandlers?.canGoBack && (
                            <button
                                type="button"
                                onClick={navigationHandlers.handlePreviousSection}
                                className="px-4 py-2 bg-blue-900 text-white rounded"
                            >
                                {t('common.back')}
                            </button>
                        )}
                        <button
                            type="button"
                            className={`main-dark-button text-sm ${createApplicantProcessMutation.isPending && '!bg-darkBlue/60'}`}
                            onClick={handleSubmit}
                            disabled={createApplicantProcessMutation.isPending || submitResponseMutation.isPending}
                        >
                            {createApplicantProcessMutation.isPending || submitResponseMutation.isPending ? t('answerApplication.submitting') : t('answerApplication.submit')}
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};

export default AnswerApplicationPage;
